import { Components } from "appworks/components/components";
import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { ButtonElement } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { SpineContainer } from "appworks/graphics/pixi/spine-container";
import { Text } from "appworks/graphics/pixi/text";
import { LoaderService } from "appworks/loader/loader-service";
import { builderManager } from "appworks/server/builders/builder-manager";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { FadeTransition } from "appworks/transitions/fade-transition";
import { MatrixSkipAreaControlFactory } from "appworks/ui/factories/standard/matrix-skip-area-control-factory";
import { UIFlagState } from "appworks/ui/flags/ui-flag-state";
import { UIFlag, UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { UI } from "appworks/ui/ui";
import { FowlFlingBonusBackgroundComponent } from "components/bonus/fling/fowl-fling-bonus-background-component";
import { FowlFlingBonusComponent } from "components/bonus/fling/fowl-fling-bonus-component";
import { FowlFlingBonusTransitionComponent } from "components/bonus/fling/fowl-fling-bonus-transition-component";
import { FowlEggPickBonusComponent } from "components/bonus/pick/fowl-egg-pick-bonus-component";
import { FowlBigWinFactory } from "components/factories/fowl-big-win-factory";
import { FowlReelMatrixFactory } from "components/factories/fowl-reel-matrix-factory";
import { FowlBackgroundFXComponent } from "components/fowl-background-fx-component";
import { FowlBetBarComponent } from "components/fowl-bet-bar-component";
import { FowlBonusTransitionComponent } from "components/fowl-bonus-transition-component";
import { FowlCelebrateWinComponent } from "components/fowl-celebrate-win-component";
import { FowlLadderComponent } from "components/fowl-ladder-component";
import { FowlSlingoReelHighlightComponent } from "components/fowl-slingo-reel-highlight-component";
import { FowlTicketMatrixComponent } from "components/fowl-ticket-matrix-component";
import { LayerNames, gameLayers } from "game-layers";
import { GMRAlertComponent } from "gaming-realms/components/gmr-alert-component";
import { GMRPreloaderComponent } from "gaming-realms/components/gmr-preloader-component";
import { GMRSuperSpinWheelComponent } from "gaming-realms/components/gmr-super-spin-wheel-component";
import { GMRCollectRequestBuilder } from "gaming-realms/integration/builders/requests/gmr-collect-request-builder";
import { GMRForceRequestBuilder } from "gaming-realms/integration/builders/requests/gmr-force-request-builder";
import { GMRInitRequestBuilder } from "gaming-realms/integration/builders/requests/gmr-init-request-builder";
import { FowlInitRecordBuilder } from "integration/builders/records/fowl-init-record-builder";
import { FowlSlotRecordBuilder } from "integration/builders/records/fowl-slot-bonus-record-builder";
import { FowlJackpotBonusResultBuilder } from "integration/builders/results/fowl-jackpot-bonus-result-builder";
import { FowlPickBonusResultBuilder } from "integration/builders/results/fowl-pick-bonus-result-builder";
import { FowlSlotBonusResultBuilder } from "integration/builders/results/fowl-slot-bonus-result-builder";
import { SlingoCelebrationComponent } from "slingo/components/slingo-celebration-component";
import { SlingoCoinHopperComponent } from "slingo/components/slingo-coin-hopper-component";
import { SlingoFooterComponent } from "slingo/components/slingo-footer-component";
import { SlingoSpinsCounterComponent } from "slingo/components/slingo-spins-counter-component";
import { SlingoCollectRecordBuilder } from "slingo/integration/builders/records/slingo-collect-record-builder";
import { SlingoPickRecordBuilder } from "slingo/integration/builders/records/slingo-pick-record-builder";
import { SlingoPurchaseEntryRecordBuilder } from "slingo/integration/builders/records/slingo-purchase-entry-record-builder";
import { SlingoSpinRecordBuilder } from "slingo/integration/builders/records/slingo-spin-record-builder";
import { SlingoStakeToSpinRecordBuilder } from "slingo/integration/builders/records/slingo-stake-to-spin-record-builder";
import { SlingoPickRequestBuilder } from "slingo/integration/builders/requests/slingo-pick-request-builder";
import { SlingoPurchaseEntryRequestBuilder } from "slingo/integration/builders/requests/slingo-purchase-entry-request-builder";
import { SlingoSpinRequestBuilder } from "slingo/integration/builders/requests/slingo-spin-request-builder";
import { SlingoStakeToSpinRequestBuilder } from "slingo/integration/builders/requests/slingo-stake-to-spin-request-builder";
import { SlingoGameProgressResultBuilder } from "slingo/integration/builders/results/slingo-game-progress-result-builder";
import { SlingoJokerResultBuilder } from "slingo/integration/builders/results/slingo-joker-result-builder";
import { SlingoLadderResultBuilder } from "slingo/integration/builders/results/slingo-ladder-result-builder";
import { SlingoReelSpinResultBuilder } from "slingo/integration/builders/results/slingo-reel-spin-result-builder";
import { SlingoStakeToSpinResultBuilder } from "slingo/integration/builders/results/slingo-stake-to-spin-result-builder";
import { SlingoSetup } from "slingo/setup/slingo-setup";
import { SlotLineWinResultBuilder } from "slot/integration/builders/results/slot-line-win-result-builder";
import { CountMode } from "slotworks/components/bigwin/big-win-component";
import { GenericComponentFactory } from "slotworks/components/factories/generic-component-factory";
import { FowlGameSummaryWinTransition } from "transitions/fowl-gamesummary-transition";
import { FowlGameDefinitionSetup } from "./fowl-game-definition-setup";
import { FowlSoundSetup } from "./fowl-sound-setup";
import { FowlStateSetup } from "./fowl-state-setup";
import { Timer } from "appworks/utils/timer";

export class FowlSetup extends SlingoSetup {
    public setupCanvas(): void {
        super.setupCanvas();
        Services.get(CanvasService).cssCanvasEnabled = true;

        Text.minPadding = 5;
    }

    public setupI18N(): void {
        super.setupI18N();

        const translations = Services.get(TranslationsService);

        translations.set("fot.gam", "");
        translations.set("fot.lin", "");

        // https://docs.google.com/spreadsheets/d/18N0H-pkuSYuKPGvW8bzUllW3oWjjczGRcXdYIsDMobg/edit?gid=0#gid=0
        // Benny says fallback to berlin sans but that doesn't support all these langs either,
        translations.setFontSubstitute("rifficfree-bold", {
            font: "sans-serif",
            includedLanguages: [
                "bg-BG",
                "cs-CZ",
                "gr-EL",
                "hr-HR",
                "hu-HU",
                "hy-AM",
                "ja-JP",
                "ka-GE",
                "ko-KR",
                "lt-LT",
                "lv-LV",
                "pl-PL",
                "pt-PT",
                "ro-RO",
                "ru-RU",
                "sk-SK",
                "sr-Latn",
                "th-TH",
                "tr-TR",
                "vi-VN",
                "zh-CN",
            ],
        });
    }

    public setupGameDefinition(): void {
        FowlGameDefinitionSetup();
    }

    public setupComponents() {
        SpineContainer.AUTO_UPDATE_SPEED = true;

        Components.register(new GenericComponentFactory(PromptComponent, { usePromptLayerAsClickListener: true }), 0);
        Components.register(new GenericComponentFactory(FowlBetBarComponent));
        Components.register(new GenericComponentFactory(FowlCelebrateWinComponent));

        Components.register(
            new FowlBigWinFactory(CountMode.LINEAR, false, [
                {
                    threshold: 0,
                    countTime: 3000,
                    waitTime: 3000,
                    scene: "win",
                },
                {
                    threshold: 20,
                    countTime: 6000,
                    waitTime: 3000,
                    scene: "bigwin",
                },
                {
                    threshold: 50,
                    countTime: 6000,
                    waitTime: 3000,
                    scene: "superwin",
                },
                {
                    threshold: 100,
                    countTime: 6000,
                    waitTime: 3000,
                    scene: "megawin",
                },
            ])
        );

        Components.register(new GenericComponentFactory(GMRPreloaderComponent), null);
        Components.register(new GenericComponentFactory(GMRAlertComponent), 0);
        Components.register(new GenericComponentFactory(GMRSuperSpinWheelComponent));

        Components.register(new FowlReelMatrixFactory(), 3);

        Components.register(new GenericComponentFactory(SlingoFooterComponent));
        Components.register(new GenericComponentFactory(SlingoCelebrationComponent, LayerNames.CelebrationContent));
        Components.register(new GenericComponentFactory(SlingoSpinsCounterComponent, LayerNames.SpinCounter));
        Components.register(new GenericComponentFactory(SlingoCoinHopperComponent));
        Components.register(new GenericComponentFactory(FowlLadderComponent));
        Components.register(new GenericComponentFactory(FowlSlingoReelHighlightComponent));

        Components.register(new GenericComponentFactory(FowlTicketMatrixComponent, { parallelCelebration: false }), 3);

        Components.register(new GenericComponentFactory(FowlFlingBonusComponent));
        Components.register(new GenericComponentFactory(FowlFlingBonusBackgroundComponent));
        Components.register(new GenericComponentFactory(FowlFlingBonusTransitionComponent));
        Components.register(new GenericComponentFactory(FowlEggPickBonusComponent));
        Components.register(new GenericComponentFactory(FowlBackgroundFXComponent));
        Components.register(new GenericComponentFactory(FowlBonusTransitionComponent));
    }

    public setupServices(): void {
        super.setupServices();

        Services.get(LoaderService).loadSoundsInPreloader = true;
    }

    public setupStates() {
        FowlStateSetup();
    }

    public setupDebug(): void {
        super.setupDebug();
        const { renderer, stage } = Services.get(CanvasService);
        globalThis.__PIXI_RENDERER__ = renderer;
        globalThis.__PIXI_STAGE__ = stage;
    }

    public setupLayers(): void {
        Layers.setLayerOrderConfig(
            {
                default: [
                    LayerNames.Background,
                    LayerNames.BackgroundPersistentAnimation,
                    LayerNames.BackgroundIntermittentAnimation,
                    LayerNames.Onboard,
                    LayerNames.EggPickBonus,
                    LayerNames.BonusUI,
                    LayerNames.EggPickPaytable,
                    LayerNames.TicketMatrixBacking,
                    LayerNames.TicketMatrix,
                    LayerNames.MatrixBackground,
                    LayerNames.MatrixContent,
                    LayerNames.MatrixForeground,
                    LayerNames.Skip,
                    LayerNames.Logo,
                    LayerNames.MenuButton,
                    LayerNames.SymbolAnimations,
                    LayerNames.SpinButton,
                    LayerNames.CoinHopper,
                    LayerNames.SpinCounter,
                    LayerNames.SlingoLadder,
                    LayerNames.Collect,
                    LayerNames.BetBar,
                    LayerNames.Footer,
                    LayerNames.SuperSpinWheel,
                    LayerNames.BonusTransition,
                    LayerNames.CelebrationBackground,
                    LayerNames.CelebrationContent,
                    LayerNames.FlingBonusBackground,
                    LayerNames.FlingBonus,
                    LayerNames.FlingBonusTransition,
                    LayerNames.FlingBonusForeground,
                    LayerNames.Prompts,
                    LayerNames.Menus,
                    LayerNames.ScreenFade,
                ],
                pickbonus: [
                    LayerNames.Onboard,
                    LayerNames.BackgroundIntermittentAnimation,
                    LayerNames.TicketMatrixBacking,
                    LayerNames.TicketMatrix,
                    LayerNames.MatrixBackground,
                    LayerNames.MatrixContent,
                    LayerNames.MatrixForeground,
                    LayerNames.Skip,
                    LayerNames.SymbolAnimations,
                    LayerNames.SpinButton,
                    LayerNames.SuperSpinWheel,
                    LayerNames.MenuButton,
                    LayerNames.CoinHopper,
                    LayerNames.SpinCounter,
                    LayerNames.FlingBonusBackground,
                    LayerNames.FlingBonus,
                    LayerNames.FlingBonusForeground,
                    LayerNames.Collect,
                    LayerNames.SlingoLadder,
                    LayerNames.Logo,
                    // everything above will be hidden
                    LayerNames.Background,
                    LayerNames.BackgroundPersistentAnimation,
                    LayerNames.EggPickBonus,
                    LayerNames.FlingBonusTransition,
                    LayerNames.BonusUI,
                    LayerNames.EggPickPaytable,
                    LayerNames.BonusTransition,
                    LayerNames.BetBar,
                    LayerNames.Footer,
                    LayerNames.CelebrationBackground,
                    LayerNames.CelebrationContent,
                    LayerNames.Prompts,
                    LayerNames.Menus,
                    LayerNames.ScreenFade,
                ],
                slotbonus: [
                    LayerNames.Onboard,
                    LayerNames.EggPickBonus,
                    LayerNames.TicketMatrixBacking,
                    LayerNames.TicketMatrix,
                    LayerNames.CoinHopper,
                    LayerNames.SpinCounter,
                    LayerNames.SlingoLadder,
                    LayerNames.FlingBonusBackground,
                    LayerNames.FlingBonus,
                    LayerNames.FlingBonusTransition,
                    LayerNames.FlingBonusForeground,
                    LayerNames.Collect,
                    LayerNames.MenuButton,
                    LayerNames.SuperSpinWheel,
                    // everything above will be hidden
                    LayerNames.Background,
                    LayerNames.BackgroundPersistentAnimation,
                    LayerNames.BackgroundIntermittentAnimation,
                    LayerNames.MatrixBackground,
                    LayerNames.MatrixContent,
                    LayerNames.MatrixForeground,
                    LayerNames.Skip,
                    LayerNames.BonusUI,
                    LayerNames.EggPickPaytable,
                    LayerNames.Logo,
                    LayerNames.SpinButton,
                    LayerNames.SymbolAnimations,
                    LayerNames.BonusTransition,
                    LayerNames.BetBar,
                    LayerNames.Footer,
                    LayerNames.CelebrationBackground,
                    LayerNames.CelebrationContent,
                    LayerNames.Prompts,
                    LayerNames.Menus,
                    LayerNames.ScreenFade,
                ],
                flingbonus: [
                    LayerNames.BackgroundPersistentAnimation,
                    LayerNames.BackgroundIntermittentAnimation,
                    LayerNames.Onboard,
                    LayerNames.EggPickBonus,
                    LayerNames.BonusUI,
                    LayerNames.EggPickPaytable,
                    LayerNames.TicketMatrixBacking,
                    LayerNames.TicketMatrix,
                    LayerNames.MatrixBackground,
                    LayerNames.MatrixContent,
                    LayerNames.MatrixForeground,
                    LayerNames.Skip,
                    LayerNames.Logo,
                    LayerNames.MenuButton,
                    LayerNames.SymbolAnimations,
                    LayerNames.SpinButton,
                    LayerNames.SuperSpinWheel,
                    LayerNames.CoinHopper,
                    LayerNames.SpinCounter,
                    LayerNames.Collect,
                    LayerNames.SlingoLadder,
                    LayerNames.BetBar,
                    LayerNames.Footer,
                    LayerNames.BonusTransition,
                    // everything above will be hidden
                    LayerNames.Background,
                    LayerNames.FlingBonusBackground,
                    LayerNames.FlingBonus,
                    LayerNames.FlingBonusTransition,
                    LayerNames.FlingBonusForeground,
                    LayerNames.CelebrationBackground,
                    LayerNames.CelebrationContent,
                    LayerNames.Prompts,
                    LayerNames.Menus,
                    LayerNames.ScreenFade,
                ],
            },
            LayerNames
        );

        gameLayers.Prompts.onSceneEnter.add(() => {
            const cover = gameLayers.Prompts.getShape("cover");
            if (cover) gameLayers.Prompts.add(cover, false, true);
        });

        gameLayers.Background.onSceneEnter.add(() => {
            const cover = gameLayers.Background.getShape("cover");
            if (cover) cover.interactive = true;
        });

        // stake btn shows over portrait ladder because it's in betbar, last minute hacks to make it look better
        gameLayers.SlingoLadder.onSceneEnter.add((scene: string) => {
            const stakeBtn = gameLayers.BetBar.getButton("ui_stake_menu_p");
            if (stakeBtn) stakeBtn.visible = scene !== "paytable";

            Timer.setTimeout(() => {
                const fakeBtn = gameLayers.SlingoLadder.getSprite("fake_stake_btn");
                if (fakeBtn) gameLayers.SlingoLadder.add(fakeBtn, false, true);
            }, 1);
        });

        // This rifficfree font needs letterspacing adjustment or it's hard to read
        Layers.getAllLayers().forEach(layer =>
            layer.onSceneEnter.add(() => {
                layer.container.children.forEach(child => {
                    let elements = [child];
                    if (child instanceof ButtonElement) {
                        elements.push(...child.getLabels());
                    }

                    for (const el of elements) {
                        if (el instanceof Text && el.style.fontFamily[0].includes("rifficfree")) {
                            el.style.letterSpacing = el.style.fontSize * 0.05;
                        }
                    }
                });
            })
        );
    }

    public setupComms(): void {
        super.setupComms();

        builderManager.addRequestBuilder(new GMRForceRequestBuilder());
        builderManager.addRequestBuilder(new GMRInitRequestBuilder());
        builderManager.addRequestBuilder(new SlingoPurchaseEntryRequestBuilder());
        builderManager.addRequestBuilder(new SlingoSpinRequestBuilder());
        builderManager.addRequestBuilder(new SlingoPickRequestBuilder());
        builderManager.addRequestBuilder(new GMRCollectRequestBuilder());
        builderManager.addRequestBuilder(new SlingoStakeToSpinRequestBuilder());

        builderManager.addRecordBuilder(new FowlInitRecordBuilder());
        builderManager.addRecordBuilder(new SlingoPurchaseEntryRecordBuilder());
        builderManager.addRecordBuilder(new SlingoSpinRecordBuilder());
        builderManager.addRecordBuilder(new SlingoPickRecordBuilder());
        builderManager.addRecordBuilder(new SlingoCollectRecordBuilder());
        builderManager.addRecordBuilder(new FowlSlotRecordBuilder());
        builderManager.addRecordBuilder(new SlingoStakeToSpinRecordBuilder());

        builderManager.addResultBuilder(new SlingoReelSpinResultBuilder());
        builderManager.addResultBuilder(new SlingoJokerResultBuilder());
        builderManager.addResultBuilder(new SlingoLadderResultBuilder());
        builderManager.addResultBuilder(new SlingoGameProgressResultBuilder());
        builderManager.addResultBuilder(new FowlJackpotBonusResultBuilder());
        builderManager.addResultBuilder(new FowlPickBonusResultBuilder());
        builderManager.addResultBuilder(new FowlSlotBonusResultBuilder());
        builderManager.addResultBuilder(new SlotLineWinResultBuilder());
        builderManager.addResultBuilder(new SlingoStakeToSpinResultBuilder());
    }

    public setupTransitions(): void {
        gameLayers.ScreenFade.setTransition(new FadeTransition(gameLayers.ScreenFade, 750));
        gameLayers.Prompts.setTransitions("scatterinfo", new FadeTransition(gameLayers.Prompts, 500, "out"));
        gameLayers.Prompts.setTransitions("gamesummary_win", new FowlGameSummaryWinTransition());
    }

    public setupUI(): void {
        UI.layer(Layers.get("BetBar"), [
            {
                scene: "bonus",
                show: new UIFlagState(UIFlag.BONUS),
                enable: UIFlagState.ALWAYS,
            },
        ]);

        UI.button(Layers.get("BetBar"), "ui_stake_menu_p", UIMenuFlag.STAKE);
        UI.button(Layers.get("MenuButton"), "ui_settings_menu_p", UIMenuFlag.MENU);

        UI.layer(Layers.get("SpinButton"), [
            {
                scene: "slotbonus",
                show: UIFlagState.FREESPINS,
                enable: new UIFlagState(UIFlag.IDLE, UIFlag.NO_SKIP),
            },
        ]);

        UI.layer(Layers.get("Skip"), [
            {
                scene: "skip",
                show: new UIFlagState(UIFlag.FREE_SPINS, UIFlag.NO_SKIP),
            },
        ]);

        UI.register(new MatrixSkipAreaControlFactory());

        super.setupUI();
    }

    public setupSound(): void {
        super.setupSound();
        FowlSoundSetup();
    }
}
